import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost } from '../../api/client';
import { PageHeader } from '../../components/common/PageHeader';
import { SelectField } from '../../components/fields/SelectField';
import { PermissionGate } from '../../components/auth/PermissionGate';
import { useAuthStore } from '../../store/auth.store';
import { ReasonDialog } from './expenses/ReasonDialog';
import { ADJUSTMENT_STATUS_LABEL, CLOSING_STATUS_LABEL, closingCountError, closingCountHint } from './branchCashLogic';

// Day closing: count the cash against the ledger's expected figure, get a significant difference approved by someone else, close (which
// locks the day), reopen with a reason, and review/approve linked cash adjustments. Buttons follow permissions; the server decides.

const unwrap = (d: any) => d?.data ?? d;
const errMsg = (e: any, fallback: string) => e?.response?.data?.error ?? fallback;
const rupees = (n: number | null | undefined) => (n === null || n === undefined ? '-' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const REFRESH = ['bc-closing', 'bc-adjustments', 'bc-current', 'bc-days', 'bc-summary', 'bc-moves'];

function AdjustmentsTable({ branchId }: { branchId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user?.id);
  const q = useQuery({ queryKey: ['bc-adjustments', branchId], queryFn: async () => unwrap(await apiGet('/branch-cash/adjustments', { branchId })) as any[] });
  const [dialog, setDialog] = useState<{ id: string; kind: 'reject' | 'cancel' } | null>(null);
  const act = useMutation({
    mutationFn: ({ id, kind, text }: { id: string; kind: 'approve' | 'reject' | 'cancel'; text?: string }) =>
      apiPost(`/branch-cash/adjustments/${id}/${kind}`, kind === 'cancel' ? { reason: text } : { note: text }),
    onSuccess: () => { REFRESH.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); setDialog(null); enqueueSnackbar('Done', { variant: 'success' }); },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not complete the action'), { variant: 'error' }),
  });
  const rows = q.data ?? [];
  if (!rows.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Cash adjustments</Typography>
      <Typography variant="caption" color="text.secondary">A posted cash entry is never edited. A correction is requested, linked to what it corrects, and posted only when someone else approves it.</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Number</TableCell><TableCell>Reason</TableCell><TableCell align="right">In</TableCell><TableCell align="right">Out</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.adjustmentNumber}</TableCell><TableCell>{a.reason}</TableCell>
                <TableCell align="right">{a.direction === 'IN' ? rupees(a.amount) : ''}</TableCell><TableCell align="right">{a.direction === 'OUT' ? rupees(a.amount) : ''}</TableCell>
                <TableCell>{ADJUSTMENT_STATUS_LABEL[a.status] ?? a.status}</TableCell>
                <TableCell>
                  {a.status === 'PENDING_APPROVAL' && (
                    <Stack direction="row" spacing={1}>
                      {a.requestedById !== me && (
                        <PermissionGate permission="cash:adjustment_approve">
                          <Button size="small" variant="contained" disabled={act.isPending} onClick={() => act.mutate({ id: a.id, kind: 'approve' })}>Approve</Button>
                          <Button size="small" color="error" onClick={() => setDialog({ id: a.id, kind: 'reject' })}>Reject</Button>
                        </PermissionGate>
                      )}
                      {a.requestedById === me && (
                        <PermissionGate permission="cash:adjustment_request"><Button size="small" onClick={() => setDialog({ id: a.id, kind: 'cancel' })}>Cancel</Button></PermissionGate>
                      )}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <ReasonDialog open={!!dialog} title={dialog?.kind === 'reject' ? 'Reject adjustment' : 'Cancel adjustment'} confirmLabel={dialog?.kind === 'reject' ? 'Reject' : 'Cancel adjustment'} busy={act.isPending}
        onClose={() => setDialog(null)} onConfirm={(text) => dialog && act.mutate({ id: dialog.id, kind: dialog.kind, text })} />
    </Paper>
  );
}

function ClosingBody({ day }: { day: any }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user?.id);
  const stateQ = useQuery({ queryKey: ['bc-closing', day.id], queryFn: async () => unwrap(await apiGet(`/branch-cash/days/${day.id}/closing`)) as any });
  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState('');
  const [dialog, setDialog] = useState<'reject' | 'reopen' | null>(null);
  const s = stateQ.data;
  const live = s?.live;
  const refresh = () => REFRESH.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const run = (label: string) => ({ onSuccess: () => { refresh(); enqueueSnackbar(label, { variant: 'success' }); }, onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not complete the action'), { variant: 'error' }) });
  const count = useMutation({ mutationFn: () => apiPost(`/branch-cash/days/${day.id}/closing/count`, { amount: counted, reason: reason.trim() || undefined }), ...run('Closing cash counted') });
  const approve = useMutation({ mutationFn: () => apiPost(`/branch-cash/closings/${live.id}/approve`, {}), ...run('Difference approved') });
  const reject = useMutation({ mutationFn: (note: string) => apiPost(`/branch-cash/closings/${live.id}/reject`, { note }), ...run('Count rejected - recount needed') });
  const close = useMutation({ mutationFn: () => apiPost(`/branch-cash/days/${day.id}/close`, {}), ...run('Business day closed') });
  const reopen = useMutation({ mutationFn: (r: string) => apiPost(`/branch-cash/days/${day.id}/reopen`, { reason: r }), ...run('Business day reopened') });
  useEffect(() => { setCounted(''); setReason(''); }, [day.id, live?.id]);
  const hint = useMemo(() => closingCountHint(s?.expectedCash ?? null, counted, s?.threshold ?? 0), [s, counted]);
  const problem = closingCountError(s?.expectedCash ?? null, counted, reason, s?.threshold ?? 0);
  if (!s) return null;
  const closed = s.status === 'CLOSED';
  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">Business day {s.businessDate}</Typography>
          <Chip size="small" color={closed ? 'default' : 'primary'} label={closed ? 'Closed' : s.status === 'REOPENED' ? 'Reopened' : 'Open'} />
        </Stack>
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="body2">Expected cash <b>{rupees(s.expectedCash)}</b></Typography>
          {live && <Typography variant="body2">Counted <b>{rupees(live.countedCash)}</b></Typography>}
          {live && <Typography variant="body2">Difference <b>{rupees(live.variance)}</b></Typography>}
          {live && <Chip size="small" label={CLOSING_STATUS_LABEL[live.status] ?? live.status} />}
        </Stack>
        {s.commitments?.approvedUnpaidExpenses?.count > 0 && <Alert severity="info" sx={{ mb: 1 }}>{s.commitments.approvedUnpaidExpenses.count} approved expense(s) totalling {rupees(s.commitments.approvedUnpaidExpenses.total)} are still unpaid - they stay outstanding after closing and do not change cash until paid.</Alert>}
        {(s.commitments?.inTransitOut?.count > 0 || s.commitments?.inTransitIn?.count > 0) && <Alert severity="info" sx={{ mb: 1 }}>Cash in transit: sent {rupees(s.commitments.inTransitOut.total)}, expected {rupees(s.commitments.inTransitIn.total)}. Cash in transit is in no branch's balance.</Alert>}
        {!closed && s.blockers.length > 0 && <Alert severity="warning" sx={{ mb: 1 }}>To close: {s.blockers.map((b: any) => `${b.message}${b.count ? ` (${b.count})` : ''}`).join('; ')}.</Alert>}

        {!closed && (
          <PermissionGate permission="cash:closing_count">
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flexWrap: 'wrap', mt: 1 }}>
              <TextField size="small" label="Counted cash (₹)" value={counted} onChange={(e) => setCounted(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" label={hint.needsReason ? 'Reason (required)' : 'Reason'} value={reason} onChange={(e) => setReason(e.target.value)} InputLabelProps={{ shrink: true }} />
              <Button variant="contained" disabled={!!problem || count.isPending} title={problem} onClick={() => count.mutate()}>{live ? 'Recount' : 'Record count'}</Button>
            </Stack>
            {hint.variance !== null && (
              <Typography variant="caption" color={hint.needsApproval ? 'warning.main' : 'text.secondary'}>
                Difference {rupees(hint.variance)}{hint.needsApproval ? ' - needs approval by another person' : hint.needsReason ? ' - needs a reason' : ''}
              </Typography>
            )}
          </PermissionGate>
        )}

        {!closed && live?.status === 'PENDING_APPROVAL' && (
          live.countedById === me ? <Alert severity="info" sx={{ mt: 1 }}>You entered this count, so another person must approve the difference.</Alert> : (
            <PermissionGate permission="cash:variance_approve">
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button variant="contained" disabled={approve.isPending} onClick={() => approve.mutate()}>Approve difference</Button>
                <Button color="error" onClick={() => setDialog('reject')}>Reject - recount</Button>
              </Stack>
            </PermissionGate>
          )
        )}
        {!closed && live && ['COUNTED', 'APPROVED'].includes(live.status) && (
          <PermissionGate permission="cash:day_close">
            <Box sx={{ mt: 1 }}><Button variant="contained" color="success" disabled={!s.canClose || close.isPending} onClick={() => close.mutate()}>Close business day</Button></Box>
          </PermissionGate>
        )}
        {closed && (
          <PermissionGate permission="cash:day_reopen">
            <Box sx={{ mt: 1 }}><Button color="warning" variant="outlined" onClick={() => setDialog('reopen')}>Reopen business day</Button></Box>
          </PermissionGate>
        )}
      </Paper>

      {s.history.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, overflowX: 'auto' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Closing history</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>#</TableCell><TableCell>Status</TableCell><TableCell align="right">Expected</TableCell><TableCell align="right">Counted</TableCell><TableCell align="right">Difference</TableCell><TableCell>Reason</TableCell><TableCell>Counted at</TableCell></TableRow></TableHead>
            <TableBody>
              {s.history.map((h: any) => (
                <TableRow key={h.id}>
                  <TableCell>{h.revision}</TableCell><TableCell>{CLOSING_STATUS_LABEL[h.status] ?? h.status}</TableCell>
                  <TableCell align="right">{rupees(h.expectedCash)}</TableCell><TableCell align="right">{rupees(h.countedCash)}</TableCell><TableCell align="right">{rupees(h.variance)}</TableCell>
                  <TableCell>{h.varianceReason ?? '-'}</TableCell><TableCell>{new Date(h.countedAt).toLocaleString('en-IN')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {s.reopens.length > 0 && <Typography variant="caption" color="text.secondary">Reopened {s.reopens.length} time(s): {s.reopens.map((r: any) => r.reason).join(' | ')}</Typography>}
        </Paper>
      )}
      <ReasonDialog open={dialog === 'reject'} title="Reject closing count" confirmLabel="Reject" busy={reject.isPending} onClose={() => setDialog(null)} onConfirm={(n) => { reject.mutate(n); setDialog(null); }} />
      <ReasonDialog open={dialog === 'reopen'} title="Reopen business day" message="The earlier closing is kept as history. Only the latest closed day can be reopened, and only before the next day has opened." confirmLabel="Reopen" busy={reopen.isPending} onClose={() => setDialog(null)} onConfirm={(r) => { reopen.mutate(r); setDialog(null); }} />
    </Stack>
  );
}

export function ClosingPage() {
  const settingsQ = useQuery({ queryKey: ['bc-settings'], queryFn: async () => unwrap(await apiGet('/branch-cash/settings')) as any[] });
  const branches = (settingsQ.data ?? []).filter((b) => b.isEnabled);
  const [branchId, setBranchId] = useState('');
  useEffect(() => { if (!branchId && branches.length) setBranchId(branches[0].id); }, [branches, branchId]);
  const currentQ = useQuery({ queryKey: ['bc-current', branchId], enabled: !!branchId, queryFn: async () => unwrap(await apiGet('/branch-cash/days/current', { branchId })) as any });
  const daysQ = useQuery({ queryKey: ['bc-days', branchId], enabled: !!branchId, queryFn: async () => unwrap(await apiGet('/branch-cash/days', { branchId, limit: 5 })) as any[] });
  const day = currentQ.data?.activeDay ?? daysQ.data?.[0];
  return (
    <Box>
      <PageHeader title="Day Closing" subtitle="Count the cash, resolve differences, close and lock the business day" />
      <Stack spacing={2}>
        <Box sx={{ maxWidth: 320 }}>
          <SelectField label="Branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} options={branches.map((b) => ({ value: b.id, label: `${b.branchCode} - ${b.name}` }))} />
        </Box>
        {!day && branchId && <Alert severity="info">No business day yet for this branch.</Alert>}
        {day && <ClosingBody day={day} />}
        {branchId && <AdjustmentsTable branchId={branchId} />}
      </Stack>
    </Box>
  );
}
